import {
  createContext,
  ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { generatedApi, Tenant } from "src/store/api/generatedApi";
import { useAvailableTenants } from "hooks/useAvailableTenantNames";
import { useParams } from "react-router";

interface AvailableTenantMetadataContextValue {
  tenantMetadataList: Tenant[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const AvailableTenantMetadataContext = createContext<
  AvailableTenantMetadataContextValue | undefined
>(undefined);

export const ProvideAvailableTenantMetadata = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { tenantGroup } = useParams<{ tenantGroup: string }>();
  const availableTenants = useAvailableTenants(tenantGroup);
  const [tenantMetadataList, setTenantMetadataList] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [getTenantMetadata] =
    generatedApi.endpoints.getTenantMetadata.useLazyQuery();

  const fetchTenantMetadata = useCallback(async () => {
    if (!availableTenants.length) {
      setTenantMetadataList([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const promises = availableTenants.map((tenant) =>
        getTenantMetadata({ tenantName: tenant })
      );

      const results = await Promise.all(promises);
      const validTenantMetadata: Tenant[] = [];

      results.forEach((result) => {
        if (result.data) {
          validTenantMetadata.push(result.data);
        } else if (result.error) {
          console.error("Failed to fetch tenant metadata:", result.error);
        }
      });

      const sortedTenantMetadata = validTenantMetadata.sort((a, b) =>
        a.long_name.localeCompare(b.long_name)
      );

      setTenantMetadataList(sortedTenantMetadata);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch tenant metadata";
      setError(errorMessage);
      console.error("Error fetching tenant metadata:", err);
    } finally {
      setIsLoading(false);
    }
  }, [availableTenants, getTenantMetadata]);

  useEffect(() => {
    void fetchTenantMetadata();
  }, [fetchTenantMetadata]);

  const value: AvailableTenantMetadataContextValue = useMemo(
    () => ({
      tenantMetadataList,
      isLoading,
      error,
      refetch: fetchTenantMetadata,
    }),
    [tenantMetadataList, isLoading, error, fetchTenantMetadata]
  );

  return (
    <AvailableTenantMetadataContext value={value}>
      {children}
    </AvailableTenantMetadataContext>
  );
};

export const useAvailableTenantMetadataContext =
  (): AvailableTenantMetadataContextValue => {
    const context = use(AvailableTenantMetadataContext);

    if (context === undefined) {
      throw new Error(
        "useAvailableTenantMetadataContext must be used within a ProvideAvailableTenantMetadata"
      );
    }

    return context;
  };
